// src/components/MessageContent.jsx
import React, { useMemo } from "react";
import { AlertCircle } from "lucide-react";

export default function MessageContent({ content, isError }) {
  // 마크다운 파싱
  const parsedContent = useMemo(() => {
    if (!content) return "";
    if (isError) return content;

    let parsed = content;

    // 코드 블록 처리
    parsed = parsed.replace(
      /```(\w+)?\n([\s\S]*?)```/g,
      (match, lang, code) => {
        return `<pre class="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-3"><code class="text-sm">${escapeHtml(
          code.trim()
        )}</code></pre>`;
      }
    );

    // 인라인 코드 처리
    parsed = parsed.replace(
      /`([^`]+)`/g,
      '<code class="bg-gray-200 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>'
    );

    // 굵은 글씨 처리
    parsed = parsed.replace(
      /\*\*([^*]+)\*\*/g,
      '<strong class="font-semibold">$1</strong>'
    );

    // 이탤릭 처리
    parsed = parsed.replace(/\*([^*]+)\*/g, "<em>$1</em>");

    // 제목 처리
    parsed = parsed.replace(
      /^### (.+)$/gm,
      '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>'
    );
    parsed = parsed.replace(
      /^## (.+)$/gm,
      '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>'
    );
    parsed = parsed.replace(
      /^# (.+)$/gm,
      '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>'
    );

    // 목록 처리
    parsed = parsed.replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>');
    parsed = parsed.replace(/(<li[^>]*>.*<\/li>\n?)+/g, (match) => {
      return `<ul class="my-2 space-y-1">${match}</ul>`;
    });

    // 숫자 목록 처리
    parsed = parsed.replace(
      /^\d+\. (.+)$/gm,
      '<li class="ml-4 list-decimal">$1</li>'
    );
    parsed = parsed.replace(
      /(<li class="ml-4 list-decimal">.*<\/li>\n?)+/g,
      (match) => {
        return `<ol class="my-2 space-y-1">${match}</ol>`;
      }
    );

    // 링크 처리
    parsed = parsed.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'
    );

    // 인용구 처리
    parsed = parsed.replace(
      /^> (.+)$/gm,
      '<blockquote class="border-l-4 border-gray-300 pl-4 italic my-2">$1</blockquote>'
    );

    // 수평선 처리
    parsed = parsed.replace(/^---$/gm, '<hr class="my-4 border-gray-300" />');

    // 줄바꿈 처리
    parsed = parsed.replace(/\n/g, "<br/>");

    return parsed;
  }, [content, isError]);

  // HTML 이스케이프 함수
  const escapeHtml = (text) => {
    const map = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  };

  if (isError) {
    return (
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span className="text-sm">{content}</span>
      </div>
    );
  }

  return (
    <div
      className="markdown-content prose prose-sm max-w-none"
      dangerouslySetInnerHTML={{ __html: parsedContent }}
      style={{
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }}
    />
  );
}
